import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Calendar,
  Clock,
  Target,
  Users,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  PlayCircle,
  Filter,
  Download,
  Share
} from 'lucide-react';
import { demoFeatures } from '../../data/demoData';
import { skyflowSampleRoadmap, veracodeSampleRoadmap } from '../../data/sampleRoadmaps';
import { Feature } from '../../types';

const TimelineView = () => {
  const [selectedQuarter, setSelectedQuarter] = useState<string>('all');
  const [selectedTeam, setSelectedTeam] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'gantt' | 'timeline'>('gantt');
  const [selectedRoadmap, setSelectedRoadmap] = useState<'demo' | 'skyflow' | 'veracode'>('skyflow');

  const quarters = ['Q1 2024', 'Q2 2024', 'Q3 2024', 'Q4 2024'];
  
  const getCurrentRoadmap = () => {
    switch (selectedRoadmap) {
      case 'skyflow':
        return skyflowSampleRoadmap;
      case 'veracode':
        return veracodeSampleRoadmap;
      default:
        return { features: demoFeatures, milestones: [] };
    }
  };

  const currentRoadmap = getCurrentRoadmap();
  const currentFeatures = currentRoadmap.features;
  const currentMilestones = 'milestones' in currentRoadmap ? currentRoadmap.milestones : [];

  const teams = Array.from(new Set(currentFeatures.map(f => f.assignedTeam)));

  const filteredFeatures = currentFeatures.filter(feature => {
    const quarterMatch = selectedQuarter === 'all' || feature.quarter === selectedQuarter;
    const teamMatch = selectedTeam === 'all' || feature.assignedTeam === selectedTeam;
    return quarterMatch && teamMatch;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'development': return 'bg-blue-500';
      case 'planning': return 'bg-orange-500';
      case 'testing': return 'bg-purple-500';
      default: return 'bg-slate-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return CheckCircle;
      case 'development': return PlayCircle;
      case 'planning': return AlertCircle;
      case 'testing': return Target;
      default: return Clock;
    }
  };

  const getMilestoneColor = (status: string) => {
    switch (status) {
      case 'completed': return 'from-green-500 to-green-600';
      case 'in-progress': return 'from-blue-500 to-blue-600';
      case 'upcoming': return 'from-orange-500 to-orange-600';
      case 'delayed': return 'from-red-500 to-red-600';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  return (
    <div className="h-full bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 p-6">
      <div className="max-w-7xl mx-auto h-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold gradient-text mb-2">
                {selectedRoadmap === 'skyflow' ? 'Skyflow Data Privacy Vault' : 
                 selectedRoadmap === 'veracode' ? 'Veracode Application Security' : 
                 'Demo'} - Timeline View
              </h1>
              <p className="text-slate-600 dark:text-slate-400">
                Enhanced product roadmap with innovative features and timeline visualization
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-lg border border-slate-200/50 dark:border-slate-700/50">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setViewMode('gantt')}
                  className={`px-4 py-2 rounded-l-lg flex items-center space-x-2 transition-all duration-200 ${
                    viewMode === 'gantt' 
                      ? 'bg-blue-500 text-white' 
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  <span>Gantt</span>
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setViewMode('timeline')}
                  className={`px-4 py-2 rounded-r-lg flex items-center space-x-2 transition-all duration-200 ${
                    viewMode === 'timeline' 
                      ? 'bg-blue-500 text-white' 
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  <Clock className="w-4 h-4" />
                  <span>Timeline</span>
                </motion.button>
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-secondary flex items-center space-x-2"
              >
                <Share className="w-4 h-4" />
                <span>Share</span>
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-primary flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Export</span>
              </motion.button>
            </div>
          </div>
          
          <div className="flex items-center space-x-4 mb-6">
            <div className="flex items-center space-x-2">
              <Target className="w-4 h-4 text-slate-600 dark:text-slate-400" />
              <select
                value={selectedRoadmap}
                onChange={(e) => setSelectedRoadmap(e.target.value as 'demo' | 'skyflow' | 'veracode')}
                className="px-3 py-2 bg-white/80 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-lg text-sm"
              >
                <option value="skyflow">Skyflow Data Privacy Vault</option>
                <option value="veracode">Veracode Application Security</option>
                <option value="demo">Demo Roadmap</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-slate-600 dark:text-slate-400" />
              <select
                value={selectedQuarter}
                onChange={(e) => setSelectedQuarter(e.target.value)}
                className="px-3 py-2 bg-white/80 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-lg text-sm"
              >
                <option value="all">All Quarters</option>
                {quarters.map(quarter => (
                  <option key={quarter} value={quarter}>{quarter}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4 text-slate-600 dark:text-slate-400" />
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                className="px-3 py-2 bg-white/80 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-lg text-sm"
              >
                <option value="all">All Teams</option>
                {teams.map(team => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 h-[calc(100vh-250px)]">
          <div className="xl:col-span-3">
            {viewMode === 'gantt' ? (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6 h-full overflow-auto"
              >
                <div className="space-y-6">
                  <div className="grid grid-cols-12 gap-4 text-sm font-medium text-slate-600 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 pb-4">
                    <div className="col-span-4">Feature</div>
                    <div className="col-span-2">Team</div>
                    <div className="col-span-1">Status</div>
                    <div className="col-span-1">Progress</div>
                    <div className="col-span-4">Timeline</div>
                  </div>
                  
                  {filteredFeatures.map((feature, index) => {
                    const StatusIcon = getStatusIcon(feature.status);
                    const progressWidth = feature.status === 'completed' ? 100 : 
                                        feature.status === 'development' ? 60 :
                                        feature.status === 'testing' ? 80 :
                                        feature.status === 'planning' ? 20 : 0;
                    
                    return (
                      <motion.div
                        key={feature.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="grid grid-cols-12 gap-4 items-center py-4 border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg transition-all duration-200"
                      >
                        <div className="col-span-4">
                          <div className="font-medium text-slate-900 dark:text-slate-100 mb-1">
                            {feature.name}
                          </div>
                          <div className="text-xs text-slate-600 dark:text-slate-400">
                            {feature.estimatedHours}h • {feature.priority}
                          </div>
                        </div>
                        
                        <div className="col-span-2">
                          <div className="flex items-center space-x-2">
                            <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                              <Users className="w-3 h-3 text-white" />
                            </div>
                            <span className="text-sm text-slate-700 dark:text-slate-300">
                              {feature.assignedTeam}
                            </span>
                          </div>
                        </div>
                        
                        <div className="col-span-1">
                          <div className="flex items-center space-x-2">
                            <StatusIcon className={`w-4 h-4 ${getStatusColor(feature.status).replace('bg-', 'text-')}`} />
                            <span className="text-xs text-slate-600 dark:text-slate-400 capitalize">
                              {feature.status}
                            </span>
                          </div>
                        </div>
                        
                        <div className="col-span-1">
                          <div className="text-center">
                            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                              {progressWidth}%
                            </div>
                          </div>
                        </div>
                        
                        <div className="col-span-4">
                          <div className="relative">
                            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3">
                              <div
                                className={`${getStatusColor(feature.status)} h-3 rounded-full flex items-center justify-end pr-2`}
                                style={{ width: `${progressWidth}%` }}
                              >
                                <span className="text-xs text-white font-medium">
                                  {feature.quarter}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6 h-full overflow-auto"
              >
                <div className="relative">
                  <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 to-purple-500"></div>
                  
                  <div className="space-y-8">
                    {quarters.map((quarter, quarterIndex) => {
                      const quarterFeatures = filteredFeatures.filter(f => f.quarter === quarter);
                      if (quarterFeatures.length === 0) return null;
                      
                      return (
                        <motion.div
                          key={quarter}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: quarterIndex * 0.2 }}
                          className="relative"
                        >
                          <div className="flex items-center space-x-4 mb-6">
                            <div className="relative z-10 w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                              <Calendar className="w-8 h-8 text-white" />
                            </div>
                            <div>
                              <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                                {quarter}
                              </h3>
                              <p className="text-sm text-slate-600 dark:text-slate-400">
                                {quarterFeatures.length} features planned
                              </p>
                            </div>
                          </div>
                          
                          <div className="ml-20 space-y-4">
                            {quarterFeatures.map((feature, featureIndex) => {
                              const StatusIcon = getStatusIcon(feature.status);
                              
                              return (
                                <motion.div
                                  key={feature.id}
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: quarterIndex * 0.2 + featureIndex * 0.1 }}
                                  className="p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 card-hover"
                                >
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="flex-1">
                                      <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
                                        {feature.name}
                                      </h4>
                                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                                        {feature.description}
                                      </p>
                                    </div>
                                    
                                    <div className="flex items-center space-x-2">
                                      <StatusIcon className={`w-5 h-5 ${getStatusColor(feature.status).replace('bg-', 'text-')}`} />
                                      <span className="text-xs text-slate-600 dark:text-slate-400 capitalize">
                                        {feature.status}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center justify-between text-sm">
                                    <div className="flex items-center space-x-4">
                                      <span className="text-slate-600 dark:text-slate-400">
                                        {feature.assignedTeam}
                                      </span>
                                      <span className="text-slate-600 dark:text-slate-400">
                                        {feature.estimatedHours}h
                                      </span>
                                    </div>
                                    
                                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      feature.priority === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                                      feature.priority === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400' :
                                      feature.priority === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                                      'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                    }`}>
                                      {feature.priority}
                                    </div>
                                  </div>
                                </motion.div>
                              );
                            })}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6"
            >
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                Upcoming Milestones
              </h3>
              
              <div className="space-y-4">
                {currentMilestones.map((milestone, index) => (
                  <motion.div
                    key={milestone.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="p-4 bg-gradient-to-r from-slate-50 to-blue-50 dark:from-slate-800 dark:to-blue-900/20 rounded-xl border border-slate-200/50 dark:border-slate-700/50"
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`w-10 h-10 bg-gradient-to-r ${getMilestoneColor(milestone.status)} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <Target className="w-5 h-5 text-white" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="font-medium text-slate-900 dark:text-slate-100 mb-1">
                          {milestone.name}
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                          {new Date(milestone.date).toLocaleDateString()}
                        </div>
                        
                        <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          milestone.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                          milestone.status === 'in-progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                          milestone.status === 'upcoming' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400' :
                          'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                        }`}>
                          {milestone.status}
                        </div>
                        
                        <div className="mt-2 text-xs text-slate-500 dark:text-slate-500">
                          {milestone.dependencies?.length || 0} dependencies
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6"
            >
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                Progress Summary
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    Total Features
                  </span>
                  <span className="font-medium text-slate-900 dark:text-slate-100">
                    {filteredFeatures.length}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    Completed
                  </span>
                  <span className="font-medium text-green-600 dark:text-green-400">
                    {filteredFeatures.filter(f => f.status === 'completed').length}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    In Development
                  </span>
                  <span className="font-medium text-blue-600 dark:text-blue-400">
                    {filteredFeatures.filter(f => f.status === 'development').length}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    Planning
                  </span>
                  <span className="font-medium text-orange-600 dark:text-orange-400">
                    {filteredFeatures.filter(f => f.status === 'planning').length}
                  </span>
                </div>
                
                <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                    Overall Progress
                  </div>
                  <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-green-500 to-blue-500 h-3 rounded-full"
                      style={{ 
                        width: `${(filteredFeatures.filter(f => f.status === 'completed').length / filteredFeatures.length) * 100}%` 
                      }}
                    ></div>
                  </div>
                  <div className="text-center text-sm font-medium text-slate-900 dark:text-slate-100 mt-2">
                    {Math.round((filteredFeatures.filter(f => f.status === 'completed').length / filteredFeatures.length) * 100)}%
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineView;